import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useRequest } from '../../../../hooks/UseRequest'
import { setModal, selectModal } from '../../../../redux/modalSlice'
import { setNotification } from '../../../../redux/notificationSlice'
import { selectDropdowns, selectDropdownOptions } from '../../../../redux/dropdownSlice'
import { selectModules } from '../../../../redux/moduleSlice'
import { triggerRerender } from '../../../../redux/rerenderSlice'
import {
  buildFilePayload,
  buildDmcRequestBody,
  parseLoanDmcResponse,
} from './dmcModalHelpers'

/**
 * Owns all state, the useForm() instance, the data-loading effect, and every
 * async handler for DmcModal. The component is a render-only shell that
 * consumes this hook and forwards the relevant pieces to the section
 * components.
 *
 * Mirrors `usePaymentPlanWizard.jsx` in shape and intent.
 */
export function useDmcModal() {
  const dispatch = useDispatch()
  const navi = useNavigate()
  const modal = useSelector(selectModal)
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const modules = useSelector(selectModules)
  const request = useRequest()

  const customerId = modal.options?.customerId
  const loanId = modal.options?.loanId
  const loanStatusId = modal.options?.loanStatusDropdownOptionId

  const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue } = useForm()

  // ---- state ---------------------------------------------------------------
  const [saving, setSaving] = useState(false)
  const [module, setModule] = useState()
  const [moduleFields, setModuleFields] = useState()
  const [stateFields, setStateFields] = useState([])

  // all files processing
  const [dmcFile, setDmcFile] = useState(null)
  const [dmcDocId, setDmcDocId] = useState(null)
  const [dmcDocTypeId, setDmcDocTypeId] = useState(null)
  const [poaFile, setPoaFile] = useState(null)
  const [poaDocId, setPoaDocId] = useState(null)
  const [poaDocTypeId, setPoaDocTypeId] = useState(null)
  const [tpcFile, setTpcFile] = useState(null)
  const [tpcDocId, setTpcDocId] = useState(null)
  const [tpcDocTypeId, setTpcDocTypeId] = useState(null)
  const [achFile, setAchFile] = useState(null)
  const [achDocId, setAchDocId] = useState(null)
  const [achDocTypeId, setAchDocTypeId] = useState(null)
  const [noticeFile, setNoticeFile] = useState(null)
  const [noticeDocId, setNoticeDocId] = useState(null)
  const [noticeDocTypeId, setNoticeDocTypeId] = useState(null)

  const [loanDmcData, setLoanDmcData] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [bankName, setBankName] = useState(null)
  const [isActive, setIsActive] = useState(true)
  const [isPoaActive, setIsPoaActive] = useState(false)
  const [disableViewBAButton, setDisableViewBAButton] = useState(false)
  const [fullBankAccountNumber, setFullBankAccountNumber] = useState(null)
  const [fullBankAccountNumberForSubmit, setFullBankAccountNumberForSubmit] = useState(null)
  const [isUpdatingBA, setIsUpdatingBA] = useState(false)
  const [haveBankDetails, setHaveBankDetails] = useState(false)

  const [showAchUploadOption, setShowAchUploadOption] = useState(false)
  const [showNoticeUploadOption, setShowNoticeUploadOption] = useState(false)
  const [selectedDmcOption, setSelectedDmcOption] = useState(null)
  const [selectedDmcHasBusinessLicense, setSelectedDmcHasBusinessLicense] = useState(null)
  const [isCreatingTempDmc, setIsCreatingTempDmc] = useState(false)
  // Identifies the customer + loan an opening is for; late responses for any other opening are dropped.
  const [loadedRequestKey, setLoadedRequestKey] = useState(null)
  const inFlightRequestKey = useRef(null)
  const active = modal.is_active && modal.type === 'dmc_modal'
  const requestKey = `${customerId}:${loanId}`

  // ---- derived -------------------------------------------------------------
  const watchDmcStatusId = watch('dmc_status_dropdown_option_id')
  const dmcStatusConfirmedId = dropdownOptions?.find((e) => e?.code_key === 'dmc_status_confirmed')?.id
  const loanStatusCurrentId = dropdownOptions?.find((e) => e?.code_key === 'loan_status_current')?.id
  const loanStatusDelinquentId = dropdownOptions?.find((e) => e?.code_key === 'loan_status_delinquent')?.id
  const showConfirmedWarning = watchDmcStatusId === dmcStatusConfirmedId
    && (loanStatusId === loanStatusCurrentId || loanStatusId === loanStatusDelinquentId)

  const fieldSets = ['dmc_status_dropdown_option_id', 'dmc_id', 'is_active', 'email_address', 'phone_number', 'address', 'address2', 'city', 'state_dropdown_option_id', 'zip_code_id']

  // ---- drop handlers -------------------------------------------------------
  const onDmcDrop = (file) => setDmcFile(file)
  const onPoaDrop = (file) => {
    setPoaFile(file)
    setValue('poa_is_active', true)
  }
  const onTpcDrop = (file) => setTpcFile(file)
  const onAchDrop = (file) => {
    setAchFile(file)
    setValue('ach_revoke_is_active', true)
  }
  const onNoticeDrop = (file) => {
    setNoticeFile(file)
    setValue('drop_notice_is_active', true)
  }

  // ---- handlers ------------------------------------------------------------
  const handleBankAccountChange = (e) => {
    if (!isCreating && !isUpdatingBA) {
      setIsUpdatingBA(true)
    }
  }

  const getCustomerDmc = async (customerId) => {
    const key = requestKey
    inFlightRequestKey.current = key
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/loan_dmc?loan_id=${loanId}`)
    try {
      const response = await request.get()
      if (inFlightRequestKey.current !== key) return
      if (response.status === 200 || response.status === 201) {
        setLoadedRequestKey(key)
        const parsed = parseLoanDmcResponse(response)
        setLoanDmcData(parsed.loanDmcData)
        setIsCreating(parsed.isCreating)
        setIsActive(parsed.isActive)
        setSelectedDmcOption(parsed.selectedDmcOption)
        setSelectedDmcHasBusinessLicense(parsed.hasBusinessLicense)
        setIsPoaActive(parsed.isPoaActive)
        if (parsed.isPoaActive) setValue('poa_is_active', true)
        setAchDocId(parsed.achDocId)
        setAchDocTypeId(parsed.achDocTypeId)
        if (parsed.achDocId) setValue('ach_revoke_is_active', true)
        setNoticeDocId(parsed.noticeDocId)
        setNoticeDocTypeId(parsed.noticeDocTypeId)
        if (parsed.noticeDocId) setValue('drop_notice_is_active', true)
        setTpcDocId(parsed.tpcDocId)
        setTpcDocTypeId(parsed.tpcDocTypeId)
        setHaveBankDetails(parsed.haveBankDetails)
        setValue('have_bank_details', parsed.haveBankDetails)
        setBankName(parsed.bankName)
        setFullBankAccountNumberForSubmit(parsed.fullBankAccountNumberForSubmit)
        setDmcDocId(parsed.dmcDocId)
        setDmcDocTypeId(parsed.dmcDocTypeId)
        setPoaDocId(parsed.poaDocId)
        setPoaDocTypeId(parsed.poaDocTypeId)
      } else {
        setLoanDmcData(null)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      if (inFlightRequestKey.current !== key) return
      setLoanDmcData(null)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const handleStateFields = (name, value, module) => {
    const stateFieldName = `${module.code_key}_${name}`;
    if (stateFields.some((item) => item.name === stateFieldName)) {
      setStateFields(stateFields.map((item) => {
        if (stateFieldName === item?.name) {
          return {
            ...item,
            value: value
          }
        } else {
          return item
        }
      }))
    } else {
      setStateFields([
        ...stateFields,
        {
          name: stateFieldName,
          value: value
        }
      ])
    }
  }

  const resetState = () => {
    setSaving(false)
    setStateFields([])
    setDmcFile(null)
    setDmcDocId(null)
    setDmcDocTypeId(null)
    setPoaFile(null)
    setPoaDocId(null)
    setPoaDocTypeId(null)
    setTpcFile(null)
    setTpcDocId(null)
    setTpcDocTypeId(null)
    setAchFile(null)
    setAchDocId(null)
    setAchDocTypeId(null)
    setNoticeFile(null)
    setNoticeDocId(null)
    setNoticeDocTypeId(null)
    setLoanDmcData(null)
    setLoadedRequestKey(null)
    setIsCreating(false)
    setBankName(null)
    setIsActive(true)
    setIsPoaActive(false)
    setDisableViewBAButton(false)
    setFullBankAccountNumber(null)
    setFullBankAccountNumberForSubmit(null)
    setIsUpdatingBA(false)
    setHaveBankDetails(false)
    setShowAchUploadOption(false)
    setShowNoticeUploadOption(false)
    setSelectedDmcOption(null)
    setSelectedDmcHasBusinessLicense(null)
    reset()
  };

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }))
    dispatch(triggerRerender({ subpanelId: 'bank_change_dashlet' }));
  };

  const onSubmit = async (formData) => {
    setSaving(true);
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/loan_dmc`);
    try {
      const body = buildDmcRequestBody(formData, {
        isCreating,
        isActive,
        isUpdatingBA,
        haveBankDetails,
        fullBankAccountNumberForSubmit,
        loanDmcData,
      });
      const file = buildFilePayload({ dmcFile, poaFile, achFile, noticeFile, tpcFile });

      if (loanId) {
        body.loan_id = loanId;
      }

      // Make the API request
      const response = await request.modify(body, file)

      // If the operation was successful, execute the success callback and reset the form
      if (response.status === 200 || response.status === 201) {
        // reload the page
        const pathSegments = window.location.pathname.split('/');
        const moduleName = pathSegments[1];
        setTimeout(() => navi(`/${moduleName}/${customerId}`, { state: { reloadPage: true } }), 2000);
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
        modal.options.successCallback()
        dispatch(triggerRerender({ subpanelId: 'document' }))
        dispatch(triggerRerender({ subpanelId: 'preferred_comm_dashlet' }))
      } else {
        // If the operation was not successful, show an error notification
        modal.options.successCallback()
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      // If an exception occurs, reset the saving state and show an error notification
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  let typingTimeout = null
  const getBankRoutingNumbers = async (inputValue, column, operator) => {
    return new Promise((resolve) => {
      if (typingTimeout) clearTimeout(typingTimeout);

      typingTimeout = setTimeout(async () => {
        const filterQuery = {
          and: [
            { column: column, value: inputValue, operator: operator },
          ],
        };

        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_routing_number?filters=${JSON.stringify(filterQuery)}`);
        const response = await request.get().then((response) => response.data.data);

        resolve(response);
      }, 1000);
    });
  };

  let dmcTypingTimeout = null
  const loadDmcOptions = async (inputValue) => {
    return new Promise((resolve) => {
      if (dmcTypingTimeout) clearTimeout(dmcTypingTimeout)
      dmcTypingTimeout = setTimeout(async () => {
        if (!inputValue) { resolve([]); return }
        const filterQuery = {
          and: [
            { column: 'is_active', value: 1, operator: '=' },
            {
              or: [
                { column: 'name', value: '%' + inputValue + "%", operator: 'like' },
                { column: 'email_address', value: '%' + inputValue + "%", operator: 'like' },
                { column: 'phone_number', value: '%' + inputValue + "%", operator: 'like' },
              ],
            },
          ],
        }
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/dmc?filters=${encodeURIComponent(JSON.stringify(filterQuery))}`)
        const response = await request.get()
        resolve(response?.data?.data || [])
      }, 1000)
    })
  }

  const handleCantFindDmc = async () => {
    if (isCreatingTempDmc) return
    setIsCreatingTempDmc(true)
    const filterQuery = {
      and: [
        { column: 'name', value: 'Temporary DMC', operator: '=' },
        { column: 'is_active', value: true, operator: '=' },
      ],
    }
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/dmc?filters=${JSON.stringify(filterQuery)}`)
    try {
      const response = await request.get()
      const temporaryDmc = response?.data?.data?.[0]
      if (temporaryDmc) {
        setValue('dmc_id', temporaryDmc.id)
        setSelectedDmcOption(temporaryDmc)
        setSelectedDmcHasBusinessLicense(temporaryDmc.has_business_license ?? null)
      } else {
        dispatch(setNotification({ type: 'error', body: 'Temporary DMC record not found.' }))
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    } finally {
      setIsCreatingTempDmc(false)
    }
  }

  // ---- effects -------------------------------------------------------------
  useEffect(() => {
    return () => {
      setLoanDmcData(null);
      setIsUpdatingBA(false);
    };
  }, []);

  // The DMC modal stays mounted in the Footer and its fields take the loaded record only as
  // defaultValue (applied once at registration). Clear the previous loan's DMC on every open and drop
  // late responses, so a previous customer's DMC/bank details can never become this one's values.
  useEffect(() => {
    if (active && customerId) {
      resetState()
      getCustomerDmc(customerId)
    } else {
      inFlightRequestKey.current = null
    }
  }, [active, customerId, loanId])

  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === 'loan_dmc')

      if (m) {
        setModule(m)

        const mf = [...m.module_fields]
        mf.sort((a, b) => a.detail_display_rank - b.detail_display_rank)
        setModuleFields(mf)
      }
    }
  }, [modules.data])

  return {
    // form
    register,
    handleSubmit,
    control,
    errors,
    setValue,
    // redux / selectors
    modal,
    dropdowns,
    dropdownOptions,
    customerId,
    loanId,
    // module + ordering
    module,
    moduleFields,
    fieldSets,
    // derived form-driven values
    watchDmcStatusId,
    dmcStatusConfirmedId,
    showConfirmedWarning,
    // state slices
    saving,
    stateFields,
    dmcFile,
    dmcDocId,
    dmcDocTypeId,
    poaFile,
    poaDocId,
    poaDocTypeId,
    tpcFile,
    tpcDocId,
    tpcDocTypeId,
    achFile,
    achDocId,
    achDocTypeId,
    noticeFile,
    noticeDocId,
    noticeDocTypeId,
    loanDmcData,
    isLoaded: loanDmcData != null && loadedRequestKey === requestKey,
    isCreating,
    bankName,
    isActive,
    isPoaActive,
    disableViewBAButton,
    fullBankAccountNumber,
    fullBankAccountNumberForSubmit,
    isUpdatingBA,
    haveBankDetails,
    showAchUploadOption,
    showNoticeUploadOption,
    selectedDmcOption,
    selectedDmcHasBusinessLicense,
    isCreatingTempDmc,
    // setters (only ones the shell or future sections need access to)
    setIsActive,
    setBankName,
    setShowAchUploadOption,
    setShowNoticeUploadOption,
    setSelectedDmcOption,
    setSelectedDmcHasBusinessLicense,
    setHaveBankDetails,
    // drop handlers
    onDmcDrop,
    onPoaDrop,
    onTpcDrop,
    onAchDrop,
    onNoticeDrop,
    // async handlers
    handleStateFields,
    handleCancel,
    onSubmit,
    getBankRoutingNumbers,
    loadDmcOptions,
    handleCantFindDmc,
    handleBankAccountChange,
  }
}
